import { Component } from 'react';
import axios from 'axios';
import { withStyles } from '@material-ui/core/styles';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import StepConnector from '@material-ui/core/StepConnector';
import Snackbar from '@material-ui/core/Snackbar';
import MuiAlert from '@material-ui/lab/Alert';
import FormSteps from './Form/FormSteps';
import EmailCheck from './Form/EmailCheck';
import RequestAccess from './Form/RequestAccess';
import ProductsServices from './Form/ProductsServices';
import Diversity from './Form/Diversity';
import UserInfo from './Form/UserInfo';
import CompanySelection from './Form/CompanySelection';
import CompanyBio from './Form/CompanyBio';
import Confirmation from './Form/Confirmation';
import { endpoints } from '../config';
import { buildRegistrationData } from '../utils/helpers';
import UserInfoState, { IUserInfoState, UserStatus } from '../types/UserInfo.types';
import DiversityState, { IDiversitySubmitState, IDiversityFile, IDiversityTagResponse } from '../types/Diversity.types';
import ProductsServicesState, { IProductsSubmitState, IProductsTagResponse } from '../types/ProductsServices.types';
import {
  IBusinessBioState,
  IBusinessInfoState,
  BusinessBioState,
  BusinessInfoState,
  IBusinessDataTagResponse,
  IBusinessTagResponse
} from '../types/CompanyBio.types';
import CompanySelectionState, { ICompanySelectionState, ICompanyOption } from '../types/CompanySelection.types';
import ConfirmationState, { IConfirmationState } from '../types/Confirmation.types';
import languageStrings from '../utils/languages/languages';
import Footer from '../components/Footer';
import CompanyData, { variableSteps, ANNUAL_SALE } from '../types/CompanyConfig.types';
import { IRequestAccessState, RequestAccessState } from '../types/RequestAccess.types';
import { StepperConfig } from '../types/StepperConfig.types';
const stepperConfig = new StepperConfig();

const ColorlibConnector = withStyles({
  alternativeLabel: {
    top: 22
  },
  active: {
    '& $line': {
      backgroundColor: '#2B83B5'
    }
  },
  completed: {
    '& $line': {
      backgroundColor: '#2B83B5'
    }
  },
  line: {
    height: 3,
    border: 0,
    backgroundColor: '#eaeaf0',
    borderRadius: 1
  }
})(StepConnector);

interface FormProps {
  step?: string;
  companyKey: string;
  companyName: string;
  portalId: string;
  contractId: string;
  okey: string;
  rkey: string;
}

interface IFormState {
  step: string;
  stepList: [];
  language: string;
  submitting: boolean;
  loadingTagged: boolean;
  response: string;
  alertOpen: boolean;
  isAlreadyTagged: boolean;
  userInfo: IUserInfoState;
  businessBio: IBusinessBioState;
  businessInfo: IBusinessInfoState;
  diversity: IDiversitySubmitState[];
  productsServices: IProductsSubmitState;
  companySelection: ICompanySelectionState;
  confirmation: IConfirmationState;
  requestAccess: IRequestAccessState;
  requiredFields: [];
}

interface ISubmitErrorResponse {
  msg: string;
}

const FormInitialState = {
  stepList: [
    stepperConfig.EmailVerification,
    stepperConfig.RequestAccess,
    stepperConfig.UserInfo,
    stepperConfig.SelectCompany,
    stepperConfig.BusinessInfo,
    stepperConfig.Diversity,
    stepperConfig.Products,
    stepperConfig.Login
  ],
  language: 'en',
  submitting: false,
  loadingTagged: false,
  response: '',
  alertOpen: false,
  isAlreadyTagged: false,
  cachedDiversityResponse: undefined,
  cachedProductsResponse: undefined,
  userInfo: UserInfoState,
  businessBio: BusinessBioState,
  businessInfo: BusinessInfoState,
  diversity: DiversityState,
  productsServices: ProductsServicesState,
  companySelection: CompanySelectionState,
  confirmation: ConfirmationState,
  requestAccess: RequestAccessState,
  requiredFields: [],
  requiredFieldsLoaded: false,
  step: stepperConfig.EmailVerification
};

export class Form extends Component<FormProps, {}> {
  state = {
    ...FormInitialState,
    step: this.props.step ? this.props.step : stepperConfig.EmailVerification
  };

  componentDidMount() {
    languageStrings.setLanguage(this.state.language);
    if (!this.state.requiredFieldsLoaded) {
      axios(`${endpoints.CompanyInfo}/registrationConfig?companyKey=${this.props.companyKey}`).then(res => {
        const companyConfig = res.data.response as CompanyData;
        const stepReceveid = companyConfig.steps;
        const stepList = this.state.stepList;

        variableSteps.forEach(step => {
          if (!stepReceveid.includes(step)) {
            const stepIndexToRemove = stepList.indexOf(step);
            delete stepList[stepIndexToRemove];
          }
        });

        //Needed to apply new indexes
        const newStepList = stepList.filter(val => val);
        this.setState(
          {
            stepList: newStepList,
            requiredFields: companyConfig.requiredFields,
            requiredFieldsLoaded: true
          },
          async () => {
            const { stepList } = this.state;
            if (stepList.includes(stepperConfig.Diversity)) {
              // fetch diversity data in step before it
              const response = await axios.get(`${endpoints.Diversity}?companyKey=${this.props.companyKey}`);
              this.setState({ cachedDiversityResponse: response });
            }

            if (stepList.includes(stepperConfig.Products)) {
              // fetch products and services data in step before it
              const response = await axios.get(`${endpoints.Products}`);
              this.setState({ cachedProductsResponse: response });
            }
          }
        );
      });

      this.handleRequestForAccess(this.props.okey);
    }
  }

  componentDidUpdate(prevProps: FormProps, prevState: IFormState) {
    languageStrings.setLanguage(this.state.language);
    if (prevState.step !== this.state.step) {
      window.scrollTo(0, 0);
    }
  }

  /**
   * Displays `RequestAccess` step if query param `okey` is present
   * @param okey The query param with the `okey` value
   */
  handleRequestForAccess = (okey: string) => {
    if (okey.trim().length <= 0) {
      this.removeStep(stepperConfig.RequestAccess);
    } else {
      // save okey in request for access
      const event = { target: { value: okey } };
      this.handleChange('requestAccess', 'offeringKey')(event);
    }
  };

  /**
   * Removes a step from the registration process
   * @param stepToRemove The step or steps to remove
   */
  removeStep = (toRemove: string | string[]) => {
    let newStepList;
    if (typeof toRemove === 'string') {
      newStepList = this.state.stepList.filter((step: string) => step !== toRemove);
    } else {
      newStepList = this.state.stepList.filter((step: string) => !toRemove.includes(step));
    }
    this.setState({ stepList: newStepList });
  };

  /**
   * Check if an existing company already have Company Info submited
   * @param value The object with the company information
   */
  fetchCompanyGlobalInfo = async (value: ICompanyOption) => {
    const { companyKey: supplierCompanyKey } = value;
    if (typeof supplierCompanyKey !== 'undefined') {
      const { companyKey } = this.props;
      const paramsInfo = { companyKey, supplierCompanyKey };
      const businessPromise = await axios.get(`${endpoints.Business}/tagged`, { params: paramsInfo });

      this.processBusinessResults(businessPromise.data);
    }
  };

  /**
   * Check if an existing company is already tag. If it is, retrieves the business, services and diversity infortmation
   * @param value The object with the company information
   */
  fetchCompanyTagInfo = async (value: ICompanyOption) => {
    const { companyKey: clientCompanyKey } = value;

    if (typeof clientCompanyKey !== 'undefined') {
      const { companyKey } = this.props;
      this.setState({ loadingTagged: true });
      try {
        const paramsTag = { companyKey, clientCompanyKey };
        const result = await axios.get(endpoints.SupplierTag, { params: paramsTag });
        const { isAlreadyTagged } = result.data;

        if (isAlreadyTagged) {
          const paramsInfo = { companyKey, supplierCompanyKey: clientCompanyKey };
          // retrieve business, services and diversity info
          const businessPromise = axios.get(`${endpoints.Business}/tagged`, { params: paramsInfo });
          const productsPromise = axios.get(`${endpoints.Products}/tagged`, { params: paramsInfo });
          const diversityPromise = axios.get(`${endpoints.Diversity}/tagged`, { params: paramsInfo });
          const results = await Promise.all([businessPromise, productsPromise, diversityPromise]);
          this.processTagInfoResults(results);
        }

        this.setState({ loadingTagged: false, isAlreadyTagged });
      } catch (err) {
        // console.error('Error retrieving supplier tag info', err.response.data);
        this.setState({ loadingTagged: false });
      }
    }
  };

  /**
   * Process the list of results of the tags endpoints to populate business, products and diversity data for an existing company
   * @param results The list of results to process of the tag endpoints
   */
  processTagInfoResults = (results: any = []) => {
    if (results && results.length) {
      this.processBusinessResults(results[0].data);
      this.processProductsResults(results[1].data);
      this.processDiversityResults(results[2].data);
    }
  };

  findAnswerFromPayload = (businessResponse: IBusinessDataTagResponse[], mappedQuestionId: number): string => {
    const answer = businessResponse.find(({ questionId }) => questionId === mappedQuestionId);
    if (answer) {
      return answer.questionAnswer.toString();
    }
    return '';
  };

  processAnnualRevenue = (businessResponse: IBusinessDataTagResponse[]) => {
    const date = new Date();
    const currentYear = date.getFullYear();
    for (let index = 1; index <= 3; index++) {
      const newAnnualRevenue = new Date(currentYear - index, date.getMonth(), date.getDate()).getFullYear();
      const value = this.findAnswerFromPayload(businessResponse, ANNUAL_SALE[newAnnualRevenue]);
      const event = { target: { value } };
      this.handleChange('businessInfo', `annualSale${index}`)(event);
    }
  };

  processBusinessResults = (businessResponse: IBusinessTagResponse) => {
    const { updatedDate, businessBiography, businessInformation } = businessResponse;

    if (businessBiography && businessBiography.length) {
      //Fill Annual Revenue
      this.processAnnualRevenue(businessBiography);
      businessBiography.forEach((businessBioItem: IBusinessDataTagResponse) => {
        const event = { target: { value: businessBioItem.questionAnswer } };

        if (businessBioItem.questionName === 'unionizedCompany') {
          businessBioItem.questionAnswer = businessBioItem.questionAnswer ? 'yes' : 'no';
        }
        this.handleChange('businessBio', businessBioItem.questionName)(event);
      });
    }

    if (businessInformation && businessInformation.length) {
      businessInformation.forEach((businessInfoItem: IBusinessDataTagResponse) => {
        const event = { target: { value: businessInfoItem.questionAnswer } };
        this.handleChange('businessInfo', businessInfoItem.questionName)(event);
      });

      this.setState((prevState: IFormState) => {
        return {
          businessBio: {
            ...prevState.businessBio,
            updatedDate: updatedDate
          }
        };
      });
    }
  };

  processProductsResults = (productsResponse: IProductsTagResponse) => {
    const { updatedDate, productList } = productsResponse;
    if (productList && productList.length) {
      const products = productList.map((product: any) => {
        return {
          itemKey: product.itemKey,
          nameToAdd: product.description
        };
      });

      this.setState((prevState: IFormState) => {
        return {
          productsServices: {
            ...prevState.productsServices,
            productsAddedList: products,
            taggedDate: updatedDate
          }
        };
      });
    }
  };

  processDiversityResults = (diversityResponse: IDiversityTagResponse) => {
    const { updatedDate, diversityFiles } = diversityResponse;
    if (diversityFiles && diversityFiles.length) {
      this.setState((prevState: IFormState) => {
        const uploadCertificates = diversityFiles.map((diversityFile: IDiversityFile) => {
          if (diversityFile.secondLevel && diversityFile.secondLevel.length > 0) {
            const { iscpuc } = diversityFile;
            return this.processDiversityResultsSecondLevel(iscpuc, diversityFile.secondLevel);
          } else {
            const { id, iscpuc, originalFileName, startDate, endDate, classification, certificateId } = diversityFile;
            return {
              ISCPUC: iscpuc,
              classification,
              referenceId: certificateId,
              endDate,
              originalFileName,
              level1Id: id,
              level2Id: null,
              startDate
            };
          }
        });
        return {
          diversity: {
            ...prevState.diversity,
            taggedDate: updatedDate,
            uploadCertificates: uploadCertificates.reduce((acc: any, val) => acc.concat(val), []),
            radioGroupValue: 'yes'
          }
        };
      });
    } else {
      const event = { target: { value: [] } };
      this.handleChange('diversity', 'uploadCertificates')(event);
    }
  };

  processDiversityResultsSecondLevel = (ISCPUC: string | undefined, diversityFiles: IDiversityFile[]) => {
    return diversityFiles.map((diversityFile: IDiversityFile) => {
      const { id, parentId, originalFileName, startDate, endDate, classification, certificateId } = diversityFile;
      return {
        ISCPUC,
        classification,
        referenceId: certificateId,
        endDate,
        originalFileName,
        level1Id: parentId,
        level2Id: id,
        startDate
      };
    });
  };

  resetForm: Function = () => {
    window.location.reload();
  };

  /**
   * Returns handleSubmit or nextStep functions depending upon the step index
   * @param stepName The name of the step
   */
  getNextStep: Function = (stepName: string): Function => {
    const { stepList } = this.state;
    const index = stepList.findIndex(step => step === stepName);
    const stepListLength = stepList.length;

    if (index === stepListLength - 1) {
      return this.handleSubmit;
    } else {
      return this.nextStep;
    }
  };

  nextStep: Function = () => {
    const { step, stepList } = this.state;
    const currentStep = stepList.indexOf(step);
    const nextStep = stepList[currentStep + 1];
    this.setState(prevState => {
      return {
        ...prevState,
        step: nextStep
      };
    });
  };

  previousStep: Function = () => {
    const { step, stepList } = this.state;
    const currentStep = stepList.indexOf(step);
    const prevStep = stepList[currentStep - 1];
    this.setState(prevState => {
      return {
        ...prevState,
        step: prevStep
      };
    });
  };

  clearTaggedData() {
    this.setState((prevState: IFormState) => {
      return {
        ...prevState,
        isAlreadyTagged: false,
        businessBio: BusinessBioState,
        businessInfo: BusinessInfoState,
        diversity: DiversityState,
        productsServices: ProductsServicesState
      };
    });
  }

  /**
   * Change handler that will update every step field in the Form state
   * @param stepName The name of the step. It must match a key name in `IFormState` e.g. 'userInfo'
   * @param fieldName The name of the field to udpdate. E.g. for 'userInfo` it must be a key of `IUserInfoState`
   * @param callback An optional function to call after updating the state
   */
  handleChange: Function = (stepName: keyof IFormState, fieldName: any, callback?: Function): Function => async (e: {
    target: { value: any };
  }) => {
    if (fieldName === 'company' && e.target.value !== null) {
      this.clearTaggedData();
      await this.fetchCompanyTagInfo(e.target.value);
      await this.fetchCompanyGlobalInfo(e.target.value);
    }

    this.setState(
      (prevState: IFormState) => {
        if (prevState.hasOwnProperty(stepName)) {
          return {
            ...prevState,
            [stepName]: {
              ...(prevState[stepName] as {}),
              [fieldName]: e.target.value
            }
          };
        }
      },
      () => {
        if (typeof callback === 'function') {
          callback();
        }
      }
    );
  };

  handleLanguageChange: Function = (language: string) => {
    languageStrings.setLanguage(language);
    this.setState(prevState => {
      return {
        ...prevState,
        language,
        cachedProductsResponse: undefined
      };
    });
  };

  /**
   * TODO: Support Auto-login after registration. This is a temporary solution to login the user
   * This will avoid redirecting the user to the Discovery Page, and use the Login JSP page instead.
   */
  handleAutoLogin = async () => {
    window.location.href = '/login.jsp';
  };

  handleSubmit: Function = () => {
    const postData = buildRegistrationData(this.state, this.props.companyKey);
    this.setState({ submitting: true, response: '', alertOpen: false });
    axios
      .post(endpoints.SubmitRegistration, postData)
      .then(res => {
        this.setState({ submitting: false, response: languageStrings.confirmation.successResponse, alertOpen: true });
        if (res.status === 201) {
          this.handleAutoLogin();
        }
      })
      .catch(
        (error: {
          response: { data: { response: string[] | ISubmitErrorResponse; message?: string; statusCode?: number } };
        }) => {
          const { response }: { response: string[] | ISubmitErrorResponse } = error.response.data;

          if (error.response.data.statusCode !== 409) {
            this.setState({ submitting: false, response: languageStrings.confirmation.errorResponse, alertOpen: true });
            return false;
          }

          if (Array.isArray(response)) {
            this.setState({ submitting: false, response: response.join(', '), alertOpen: true });
          } else {
            const { statusCode } = error.response.data;
            const errorMessage = statusCode === 409 ? languageStrings.confirmation.usernameTaken : response.msg;
            this.setState({ submitting: false, response: errorMessage, alertOpen: true });
          }
        }
      );
  };

  getSteps: Function = () => {
    return languageStrings.steps;
  };

  getStepContent: Function = (step: string) => {
    switch (step) {
      case stepperConfig.EmailVerification:
        return (
          <EmailCheck
            nextStep={this.getNextStep(stepperConfig.EmailVerification)}
            handleChange={this.handleChange}
            email={this.state.userInfo.emailAddress}
            handleLanguageChange={this.handleLanguageChange}
            language={this.state.language}
            stepList={this.state.stepList}
            companyKey={this.props.companyKey}
            companyName={this.props.companyName}
            languageStrings={languageStrings}
            onStepRemove={this.removeStep}
            okey={this.props.okey}
            rkey={this.props.rkey}
            portalId={this.props.portalId}
            contractId={this.props.contractId}
          />
        );

      case stepperConfig.RequestAccess:
        return (
          <RequestAccess
            nextStep={this.getNextStep(stepperConfig.RequestAccess)}
            previousStep={this.previousStep}
            handleChange={this.handleChange}
            companyKey={this.props.companyKey}
            languageStrings={languageStrings}
            data={this.state.requestAccess}
          />
        );

      case stepperConfig.UserInfo:
        return (
          <UserInfo
            previousStep={this.resetForm}
            nextStep={this.getNextStep(stepperConfig.UserInfo)}
            handleChange={this.handleChange}
            companyKey={this.props.companyKey}
            data={this.state.userInfo}
            languageStrings={languageStrings}
          />
        );

      case stepperConfig.SelectCompany:
        return (
          <CompanySelection
            previousStep={this.previousStep}
            nextStep={this.getNextStep(stepperConfig.SelectCompany)}
            handleChange={this.handleChange}
            data={this.state.companySelection}
            loadingTagged={this.state.loadingTagged}
            languageStrings={languageStrings}
            emailAddress={this.state.userInfo.emailAddress}
            isInvited={this.state.userInfo.status === UserStatus.invited || false}
          />
        );

      case stepperConfig.BusinessInfo:
        return (
          <CompanyBio
            previousStep={this.previousStep}
            nextStep={this.getNextStep(stepperConfig.BusinessInfo)}
            handleChange={this.handleChange}
            dataBusinessBio={this.state.businessBio}
            dataBusinessInfo={this.state.businessInfo}
            languageStrings={languageStrings}
            isAlreadyTagged={this.state.isAlreadyTagged}
            taggedDate={this.state.businessBio.updatedDate}
            requiredFields={this.state.requiredFields}
          />
        );
      case stepperConfig.Diversity:
        return (
          <Diversity
            nextStep={this.getNextStep(stepperConfig.Diversity)}
            previousStep={this.previousStep}
            handleChange={this.handleChange}
            companyKey={this.props.companyKey}
            initialState={this.state.diversity}
            cachedResponse={this.state.cachedDiversityResponse}
            isAlreadyTagged={this.state.isAlreadyTagged}
            taggedDate={this.state.diversity.taggedDate}
            languageStrings={languageStrings}
          />
        );

      case stepperConfig.Products:
        return (
          <ProductsServices
            nextStep={this.getNextStep(stepperConfig.Products)}
            previousStep={this.previousStep}
            handleChange={this.handleChange}
            initialState={this.state.productsServices}
            cachedResponse={this.state.cachedProductsResponse}
            isAlreadyTagged={this.state.isAlreadyTagged}
            taggedDate={this.state.productsServices.taggedDate}
            languageStrings={languageStrings}
          />
        );

      case stepperConfig.Login:
        return (
          <Confirmation
            previousStep={this.previousStep}
            nextStep={this.handleSubmit}
            handleChange={this.handleChange}
            submitting={this.state.submitting}
            data={this.state.confirmation}
            languageStrings={languageStrings}
          />
        );

      default:
        return (
          <div>
            <a href="/">Unknown step</a>
          </div>
        );
    }
  };

  render() {
    const { step, response, alertOpen, stepList } = this.state;
    const steps = this.getSteps();
    const alertSeverity = response === 'Supplier registered successfully' ? 'info' : 'error';
    return (
      <div className="Form">
        <Stepper
          className={this.state.submitting ? 'Stepper-submitting' : ''}
          alternativeLabel
          activeStep={stepList.indexOf(step)}
          connector={<ColorlibConnector />}
        >
          {stepList.map(value => {
            return (
              <Step key={value}>
                <StepLabel
                  StepIconComponent={(props: any) => (
                    <FormSteps {...props} step={value} stepperConfig={stepperConfig} />
                  )}
                >
                  {steps[value]}
                </StepLabel>
              </Step>
            );
          })}
        </Stepper>
        <div className="step">{this.getStepContent(step)}</div>
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={alertOpen}
          autoHideDuration={6000}
          onClose={() => this.setState({ alertOpen: false })}
        >
          <MuiAlert onClose={() => this.setState({ alertOpen: false })} severity={alertSeverity}>
            {response}
          </MuiAlert>
        </Snackbar>
        <Footer languageStrings={languageStrings} />
      </div>
    );
  }
}

export default Form;
